import {
  base64URLStringToBuffer,
  Base64URLString,
} from '@simplewebauthn/browser'
import { encodeFunctionData, Hex } from 'viem'

import {
  BackendTransaction,
  OrgTransaction,
  OrgRegularTransaction,
  OrgSmartAccount,
  BackendSmartAccount,
} from '~/types'
import { getReadPublicClient } from '~/utils/requests'

export const EIP155_PREFIX = 'eip155:'

export const getTransactionGasCost = async (
  transaction: Pick<
    OrgRegularTransaction,
    | 'chainId'
    | 'callGasLimit'
    | 'verificationGasLimit'
    | 'preVerificationGas'
    | 'maxFeePerGas'
    | 'maxPriorityFeePerGas'
    | 'transactionHash'
  >,
) => {
  const publicClient = getReadPublicClient(transaction.chainId)

  let blockNumber
  if (transaction.transactionHash) {
    const txData = await publicClient.getTransaction({
      hash: transaction.transactionHash as Hex,
    })
    blockNumber = txData.blockNumber
  }

  const block = await publicClient.getBlock({ blockNumber })

  const minFee =
    block.baseFeePerGas &&
    block.baseFeePerGas + BigInt(transaction.maxPriorityFeePerGas) <
      BigInt(transaction.maxFeePerGas)
      ? block.baseFeePerGas + BigInt(transaction.maxPriorityFeePerGas)
      : BigInt(transaction.maxFeePerGas)

  const transactionCost =
    minFee *
    (BigInt(transaction.preVerificationGas) +
      BigInt(transaction.verificationGasLimit) +
      BigInt(transaction.callGasLimit))

  return transactionCost
}

// keep in sync with server function
export const appendEmailToAuthenticatorName = (
  passkeyName: string,
  passkeyEmail?: string,
) => {
  return passkeyEmail ? `${passkeyEmail} (${passkeyName})` : passkeyName
}

// properly format smart account name
export const getSmartAccountName = (
  smartAccount: OrgSmartAccount,
  displayName?: string, // optional fallback
) => {
  return smartAccount.name ?? displayName
}

type Namespace = {
  eip155?: {
    chains?: string[]
    accounts?: string[]
  }
}
export const extractNamespaceChains = (namespace?: Namespace) => {
  return (
    namespace?.eip155?.chains?.map((eipChain) =>
      parseInt(eipChain.slice(EIP155_PREFIX.length)),
    ) ?? []
  )
}
export const extractAccountFromNamespace = (namespace?: Namespace) => {
  const parts = namespace?.eip155?.accounts?.[0].split(':')
  return parts ? parts[parts.length - 1] : undefined
}

export const isTransactionOrg = (
  transaction: BackendTransaction,
): transaction is OrgTransaction => {
  return transaction.isSplitsTransaction && !transaction.isPublicTransaction
}

export const isSmartAccountOrg = (
  smartAccount?: BackendSmartAccount,
): smartAccount is OrgSmartAccount => {
  return !!smartAccount && !smartAccount.isPublicSmartAccount
}

export const getDeployCreateData = (data: Hex) => {
  return encodeFunctionData({
    abi: [
      {
        inputs: [{ internalType: 'bytes', name: 'initCode_', type: 'bytes' }],
        name: 'deployCreate',
        outputs: [
          {
            internalType: 'address',
            name: 'newContract',
            type: 'address',
          },
        ],
        stateMutability: 'payable',
        type: 'function',
      },
    ],
    functionName: 'deployCreate',
    args: [data ?? '0x'],
  })
}

export const convertChallengeToOriginalHash = (challenge: Base64URLString) => {
  const arrayBuffer = base64URLStringToBuffer(challenge)
  const uint8Array = new Uint8Array(arrayBuffer)
  return ('0x' +
    Array.from(uint8Array)
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('')) as Hex
}
